import { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

export default function AdminRatings({ user, onLogout }) {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [kudos, setKudos] = useState([]);
  const [loadingRatings, setLoadingRatings] = useState(false);
  const [editingRating, setEditingRating] = useState(null);
  const [newRatingValue, setNewRatingValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRater, setFilterRater] = useState('');
  const [filterRated, setFilterRated] = useState('');
  const [players, setPlayers] = useState([]);
  const [insights, setInsights] = useState(null);
  const [showOptedOut, setShowOptedOut] = useState(false);

  useEffect(() => {
    loadGames();
    api.get('/admin/rating-participation/stats')
      .then((r) => setInsights(r.data))
      .catch(() => {});
  }, []);

  // Reload ratings when filters change
  useEffect(() => {
    if (selectedGame) {
      loadGameRatings(selectedGame.id);
    }
  }, [filterRater, filterRated]);


  const loadGames = async () => {
    try {
      const response = await api.get('/games');
      // Only show games that have ended (is_archived or past date)
      const pastGames = response.data.filter(game => {
        const gameDate = new Date(game.date);
        const now = new Date();
        return game.is_archived || gameDate < now;
      });
      // Sort by date descending
      pastGames.sort((a, b) => new Date(b.date) - new Date(a.date));
      setGames(pastGames);
    } catch (error) {
      toast.error('Failed to load games');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadGameRatings = async (gameId) => {
    setLoadingRatings(true);
    try {
      // Build query params for filters
      const params = new URLSearchParams();
      if (filterRater) params.append('filter_rater', filterRater);
      if (filterRated) params.append('filter_rated', filterRated);
      
      const [ratingsResponse, summaryResponse] = await Promise.all([
        api.get(`/admin/games/${gameId}/all-ratings${params.toString() ? '?' + params.toString() : ''}`),
        api.get(`/games/${gameId}/ratings-summary`)
      ]);
      
      // Store the full ratings response data
      setRatings({
        ratings: ratingsResponse.data.ratings || [],
        game_experience_ratings: ratingsResponse.data.game_experience_ratings || [],
        game_average_rating: ratingsResponse.data.game_average_rating
      });
      setKudos(ratingsResponse.data.kudos || []);
      setPlayers(ratingsResponse.data.players || []); // Set players for filter dropdowns
      
      // Add summary info to selected game
      const game = games.find(g => g.id === gameId);
      if (game) {
        setSelectedGame({
          ...game,
          summary: summaryResponse.data,
          enable_player_rating: ratingsResponse.data.enable_player_rating
        });
      }
    } catch (error) {
      toast.error('Failed to load ratings');
      console.error(error);
    } finally {
      setLoadingRatings(false);
    }
  };

  const handleDeleteRating = async (ratingId) => {
    if (!confirm('Are you sure you want to delete this rating? This will recalculate community ratings.')) {
      return;
    }

    try {
      await api.delete(`/admin/ratings/${ratingId}`);
      toast.success('Rating deleted successfully');
      // Reload ratings for current game
      if (selectedGame) {
        loadGameRatings(selectedGame.id);
      }
    } catch (error) {
      toast.error('Failed to delete rating');
      console.error(error);
    }
  };

  const handleBulkDeleteByRater = async () => {
    if (!selectedGame || !filterRater) return;
    const rater = players.find((p) => p.id === filterRater);
    const raterName = rater?.name || 'this player';
    const count = (ratings.ratings || []).length;
    if (count === 0) {
      toast.info('No ratings to delete for this rater in this game.');
      return;
    }
    if (!confirm(
      `Delete ALL ${count} rating${count === 1 ? '' : 's'} (and kudos) given by ${raterName} in "${selectedGame.title}"?\n\n` +
      'Community ratings of affected players will be recalculated. This cannot be undone.'
    )) return;

    try {
      const resp = await api.delete(
        `/admin/games/${selectedGame.id}/ratings/by-rater/${filterRater}`
      );
      const { ratings_deleted = 0, kudos_deleted = 0 } = resp.data || {};
      toast.success(`Deleted ${ratings_deleted} rating(s) and ${kudos_deleted} kudos given by ${raterName}`);
      loadGameRatings(selectedGame.id);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Bulk delete failed');
      console.error(error);
    }
  };

  const handleEditRating = (rating) => {
    setEditingRating(rating);
    setNewRatingValue(rating.rating.toString());
  };

  const handleSaveEdit = async () => {
    const newValue = parseInt(newRatingValue);
    if (isNaN(newValue) || newValue < 1 || newValue > 10) {
      toast.error('Rating must be between 1 and 10');
      return;
    }

    try {
      // Update rating via API - use rating_id field
      await api.put(`/admin/ratings/${editingRating.rating_id}`, {
        rating: newValue
      });
      toast.success('Rating updated successfully');
      setEditingRating(null);
      // Reload ratings
      if (selectedGame) {
        loadGameRatings(selectedGame.id);
      }
    } catch (error) {
      toast.error('Failed to update rating');
      console.error(error);
    }
  };

  const getKudosIcon = (type) => {
    const icons = {
      teamwork: '🤝',
      positive_energy: '😊',
      supportive: '💪',
      great_spike: '🏐',
      great_serve: '💥',
      great_pass: '🎯',
      great_defense: '🛡️',
      strong_block: '🚫',
      great_setting: '🙌',
      // Legacy types for backward compatibility
      friendly: '😊',
      performance: '⚡',
      communication: '💬'
    };
    return icons[type] || '⭐';
  };

  const filteredGames = games.filter(game =>
    game.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    game.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
    game.date.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-xl text-gray-600">Loading games...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            ⭐ Rating Management
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            View, edit, and delete player ratings to maintain fair play
          </p>
        </div>

        {/* Rating Participation Insights */}
        {insights && (
          <div
            className="bg-white rounded-2xl shadow-lg p-5 sm:p-6 mb-6 flex flex-col sm:flex-row items-center justify-between gap-4"
            data-testid="participation-insights"
          >
            <div className="flex items-center gap-3">
              <span className="text-3xl">⭐</span>
              <div>
                <h3 className="font-bold text-gray-900">Rating Participation</h3>
                <p className="text-sm text-gray-500">Privacy trend across approved members</p>
              </div>
            </div>
            <div className="flex gap-3 sm:gap-4 flex-wrap justify-center">
              <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-2 text-center">
                <div className="text-2xl font-bold text-blue-700" data-testid="insights-total-members">{insights.total_members}</div>
                <div className="text-xs text-blue-600">Members</div>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-2 text-center">
                <div className="text-2xl font-bold text-green-700" data-testid="insights-participating">{insights.participating}</div>
                <div className="text-xs text-green-600">Participating</div>
              </div>
              <button
                onClick={() => setShowOptedOut(true)}
                data-testid="insights-opted-out"
                title="Click to see who opted out"
                className="bg-red-50 border border-red-200 rounded-xl px-4 py-2 text-center cursor-pointer hover:bg-red-100 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-400 transition-colors"
              >
                <div className="text-2xl font-bold text-red-600">{insights.opted_out}</div>
                <div className="text-xs text-red-500 underline decoration-dotted">Opted out ({insights.opted_out_percent}%)</div>
              </button>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="mb-6">
          <Input
            type="text"
            placeholder="Search games by title, location, or date..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
        </div>

        {/* Games List */}
        <div className="grid gap-4">
          {filteredGames.map((game) => (
            <Card key={game.id} className="shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                  <div className="flex-1">
                    <CardTitle className="text-xl sm:text-2xl mb-2">{game.title}</CardTitle>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>📍 {game.location}</div>
                      <div>📅 {game.date} • {game.start_time} - {game.end_time}</div>
                      {game.mvp_name && (
                        <div className="flex items-center gap-2">
                          <span>🏆 MVP: {game.mvp_name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    onClick={() => {
                      if (selectedGame?.id === game.id) {
                        setSelectedGame(null);
                      } else {
                        loadGameRatings(game.id);
                      }
                    }}
                    className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    {selectedGame?.id === game.id ? 'Hide Ratings' : 'View Ratings'}
                  </Button>
                </div>
              </CardHeader>

              {/* Ratings Display */}
              {selectedGame?.id === game.id && (
                <CardContent className="p-4 sm:p-6">
                  {loadingRatings ? (
                    <div className="text-center py-8 text-gray-600">Loading ratings...</div>
                  ) : (
                    <>
                      {/* Game Summary */}
                      {selectedGame.summary && (
                        <div className="mb-6 p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                          <h3 className="font-bold text-lg mb-3">Game Summary</h3>
                          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-sm">
                            <div>
                              <div className="text-gray-600">Total Ratings</div>
                              <div className="text-2xl font-bold text-blue-600">
                                {selectedGame.summary.total_ratings || 0}
                              </div>
                            </div>
                            <div>
                              <div className="text-gray-600">Avg Rating</div>
                              <div className="text-2xl font-bold text-green-600">
                                {selectedGame.summary.average_rating?.toFixed(2) || 'N/A'}
                              </div>
                            </div>
                            <div>
                              <div className="text-gray-600">Total Kudos</div>
                              <div className="text-2xl font-bold text-purple-600">
                                {kudos.length}
                              </div>
                            </div>
                            <div>
                              <div className="text-gray-600">Players Rated</div>
                              <div className="text-2xl font-bold text-orange-600">
                                {Object.keys(selectedGame.summary.player_averages || {}).length}
                              </div>
                            </div>
                            <div>
                              <div className="text-gray-600">Game Rating</div>
                              <div className="text-2xl font-bold text-yellow-600">
                                {selectedGame.summary.game_experience_rating?.toFixed(2) || 'N/A'}/10
                              </div>
                              <div className="text-xs text-gray-500">
                                ({selectedGame.summary.game_ratings_count || 0} votes)
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Game Experience Ratings */}
                      {ratings.game_experience_ratings && ratings.game_experience_ratings.length > 0 && (
                        <div className="mb-6 p-4 bg-yellow-50 rounded-lg border-2 border-yellow-200">
                          <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                            <span>⭐</span>
                            Game Experience Ratings
                          </h3>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {ratings.game_experience_ratings.map((gr, idx) => (
                              <div key={idx} className="bg-white p-3 rounded-lg border border-yellow-300">
                                <div className="font-medium text-sm text-gray-900">{gr.user_name}</div>
                                <div className="text-2xl font-bold text-yellow-600">{gr.rating}/10</div>
                              </div>
                            ))}
                          </div>
                          <div className="mt-4 text-center">
                            <span className="text-lg font-bold text-yellow-700">
                              Average: {ratings.game_average_rating}/10
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Filter Controls */}
                      {players.length > 0 && (
                        <div className="mb-6 p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
                          <h3 className="font-bold text-lg mb-3">Filter Ratings</h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Filter by Rater (who gave ratings)
                              </label>
                              <select
                                value={filterRater}
                                onChange={(e) => setFilterRater(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                              >
                                <option value="">All Players</option>
                                {players.map((player) => (
                                  <option key={player.id} value={player.id}>
                                    {player.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Filter by Rated Player (who was rated)
                              </label>
                              <select
                                value={filterRated}
                                onChange={(e) => setFilterRated(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                              >
                                <option value="">All Players</option>
                                {players.map((player) => (
                                  <option key={player.id} value={player.id}>
                                    {player.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          
                          {/* Active Filters Display */}
                          {(filterRater || filterRated) && (
                            <div className="mt-4 flex flex-wrap gap-2 items-center">
                              <span className="text-sm font-medium text-gray-700">Active Filters:</span>
                              {filterRater && (
                                <Badge className="bg-blue-500 text-white flex items-center gap-2">
                                  Rater: {players.find(p => p.id === filterRater)?.name}
                                  <button
                                    onClick={() => setFilterRater('')}
                                    className="ml-1 hover:text-gray-200"
                                  >
                                    ✕
                                  </button>
                                </Badge>
                              )}
                              {filterRated && (
                                <Badge className="bg-green-500 text-white flex items-center gap-2">
                                  Rated: {players.find(p => p.id === filterRated)?.name}
                                  <button
                                    onClick={() => setFilterRated('')}
                                    className="ml-1 hover:text-gray-200"
                                  >
                                    ✕
                                  </button>
                                </Badge>
                              )}
                              <Button
                                onClick={() => {
                                  setFilterRater('');
                                  setFilterRated('');
                                }}
                                size="sm"
                                variant="outline"
                                className="text-xs"
                              >
                                Clear All
                              </Button>
                              {filterRater && (
                                <Button
                                  data-testid="bulk-delete-by-rater-btn"
                                  onClick={handleBulkDeleteByRater}
                                  size="sm"
                                  variant="destructive"
                                  className="text-xs ml-auto"
                                  title="Delete ALL ratings given by this rater in this game"
                                >
                                  🗑️ Delete All Ratings by {players.find(p => p.id === filterRater)?.name || 'Rater'}
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Rating Status Badge */}
                      {selectedGame?.enable_player_rating === false && (
                        <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 rounded-lg text-yellow-800">
                          <span className="font-semibold">⚠️ Note:</span> Player rating was disabled for this game.
                        </div>
                      )}

                      {/* Ratings Table */}
                      {ratings.ratings && ratings.ratings.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse">
                            <thead>
                              <tr className="border-b-2 border-gray-300 bg-gray-50">
                                <th className="text-left p-3 text-xs sm:text-sm font-bold">Rater</th>
                                <th className="text-left p-3 text-xs sm:text-sm font-bold">Rated Player</th>
                                <th className="text-center p-3 text-xs sm:text-sm font-bold">Rating</th>
                                <th className="text-left p-3 text-xs sm:text-sm font-bold">Kudos</th>
                                <th className="text-center p-3 text-xs sm:text-sm font-bold">Date</th>
                                <th className="text-center p-3 text-xs sm:text-sm font-bold">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ratings.ratings.map((rating) => (
                                <tr key={rating.id} className="border-b border-gray-200 hover:bg-gray-50">
                                  <td className="p-3">
                                    <div className="text-xs sm:text-sm font-medium text-gray-900">
                                      {rating.rater_name}
                                    </div>
                                    <div className="text-xs text-gray-500">{rating.rater_id.slice(0, 8)}...</div>
                                  </td>
                                  <td className="p-3">
                                    <div className="text-xs sm:text-sm font-medium text-gray-900">
                                      {rating.rated_player_name}
                                    </div>
                                    <div className="text-xs text-gray-500">{rating.rated_player_id.slice(0, 8)}...</div>
                                  </td>
                                  <td className="p-3 text-center">
                                    <Badge className="bg-green-500 text-base sm:text-lg font-bold px-3 py-1">
                                      {rating.rating}/10
                                    </Badge>
                                  </td>
                                  <td className="p-3">
                                    <div className="flex flex-wrap gap-1">
                                      {kudos
                                        .filter(k => k.giver_id === rating.rater_id && k.recipient_id === rating.rated_player_id)
                                        .map((k, idx) => (
                                          <span key={idx} className="text-lg" title={k.kudos_type}>
                                            {getKudosIcon(k.kudos_type)}
                                          </span>
                                        ))}
                                      {kudos.filter(k => k.giver_id === rating.rater_id && k.recipient_id === rating.rated_player_id).length === 0 && (
                                        <span className="text-xs text-gray-400">No kudos</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="p-3 text-center text-xs text-gray-600">
                                    {new Date(rating.created_at).toLocaleDateString()}
                                  </td>
                                  <td className="p-3">
                                    <div className="flex gap-2 justify-center">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleEditRating(rating)}
                                        className="text-xs"
                                      >
                                        ✏️ Edit
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={() => handleDeleteRating(rating.rating_id)}
                                        className="text-xs"
                                      >
                                        🗑️ Delete
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-12 text-gray-500">
                          No ratings submitted for this game yet
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              )}
            </Card>
          ))}

          {filteredGames.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No games found matching your search
            </div>
          )}
        </div>
      </div>

      {/* Opted-out Users Dialog */}
      <Dialog open={showOptedOut} onOpenChange={setShowOptedOut}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="opted-out-users-dialog">
          <DialogHeader>
            <DialogTitle>🔒 Opted-out players</DialogTitle>
            <DialogDescription>
              These members disabled rating participation. Their ratings and kudos are preserved but hidden from other players.
            </DialogDescription>
          </DialogHeader>
          {insights?.opted_out_users?.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {insights.opted_out_users.map((u) => (
                <div key={u.id} className="py-3 flex items-center justify-between gap-3" data-testid={`opted-out-user-${u.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{u.name}</div>
                    <div className="text-xs text-gray-500 truncate">{u.email}</div>
                  </div>
                  <div className="text-right text-xs text-gray-500 flex-shrink-0">
                    {u.rating > 0 && <div>Admin rating: <span className="font-semibold text-gray-700">{u.rating}</span></div>}
                    {u.community_rating_average > 0 && <div>Community: <span className="font-semibold text-gray-700">{u.community_rating_average.toFixed(2)}</span></div>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500" data-testid="opted-out-empty">
              No players have opted out — everyone is participating! 🎉
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Rating Dialog */}
      <Dialog open={!!editingRating} onOpenChange={() => setEditingRating(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Rating</DialogTitle>
            <DialogDescription>
              Change the rating value (1-10)
            </DialogDescription>
          </DialogHeader>
          {editingRating && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Rater:</strong> {editingRating.rater_name}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Rated Player:</strong> {editingRating.rated_player_name}
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  <strong>Current Rating:</strong> {editingRating.rating}/10
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">New Rating</label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={newRatingValue}
                  onChange={(e) => setNewRatingValue(e.target.value)}
                  className="w-full"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() => setEditingRating(null)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveEdit}
                  className="flex-1 bg-green-500 hover:bg-green-600"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
